import React from 'react';

import {
  AdjusterDetail,
  BenefitDetail,
  CarrierDetail,
  ClaimDetail,
  ContactHandleSchema,
  CoverageSchema,
  PolicyIdentifier,
  VehicleDetail,
} from '@/services/api/generated/matterChangesService';

import { ActivityDescription } from './ActivityDescription';
import { CollapsibleList } from './CollapsibleList';
import { formatCoverageValue, formatEnumValue, formatFieldName } from './utils';

export interface CoverageFieldsChangedProps {
  previous: CoverageSchema | null | undefined;
  current: CoverageSchema | null | undefined;
}

/**
 * Renders only the fields that changed between previous and current coverage.
 * Shows the current (new) values for changed fields with full detail.
 */
export const CoverageFieldsChanged: React.FC<CoverageFieldsChangedProps> = ({
  previous,
  current,
}) => {
  if (!current) return null;

  // If no previous, show all current fields (initial population)
  if (!previous) {
    return (
      <div className="space-y-2">
        <div className="text-sm text-gray-500 italic">Initial values set:</div>
        <CoverageFieldsDisplay coverage={current} />
      </div>
    );
  }

  // Define which fields to check for changes
  const fieldsToCheck: (keyof CoverageSchema)[] = [
    'policy_type',
    'carrier',
    'policy_identifiers',
    'category',
    'named_insured',
    'benefits',
    'adjusters',
    'claims',
    'vehicles',
    'policy_details',
    'subrogator',
    'status',
  ];

  const changedFields = fieldsToCheck
    .map((fieldName) => {
      const prevValue = previous[fieldName];
      const currValue = current[fieldName];

      // Check if value changed
      const hasChanged =
        JSON.stringify(prevValue) !== JSON.stringify(currValue);

      if (!hasChanged) return null;

      // Skip if current value is null/undefined
      if (currValue === null || currValue === undefined) return null;

      // Skip empty arrays
      if (Array.isArray(currValue) && currValue.length === 0) return null;

      return {
        name: fieldName,
        value: currValue,
      };
    })
    .filter((field): field is NonNullable<typeof field> => field !== null);

  if (changedFields.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic">No visible changes</div>
    );
  }

  // Separate simple fields from complex collapsible fields
  const complexFieldNames = [
    'carrier',
    'policy_identifiers',
    'benefits',
    'adjusters',
    'claims',
    'vehicles',
  ];

  const simpleFields = changedFields.filter(
    (field) => !complexFieldNames.includes(field.name as string),
  );
  const complexFields = changedFields.filter((field) =>
    complexFieldNames.includes(field.name as string),
  );

  return (
    <div className="space-y-2">
      <div className="space-y-2">
        {/* Simple fields first */}
        {simpleFields.map((field) => (
          <CoverageFieldDisplay
            key={String(field.name)}
            name={field.name}
            value={field.value}
          />
        ))}
        {/* Complex collapsible fields at the bottom */}
        {complexFields.map((field) => (
          <CoverageFieldDisplay
            key={String(field.name)}
            name={field.name}
            value={field.value}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Helper component to display all coverage fields (used when previous is null)
 */
const CoverageFieldsDisplay: React.FC<{ coverage: CoverageSchema }> = ({
  coverage,
}) => {
  const fieldsToDisplay: (keyof CoverageSchema)[] = [
    'policy_type',
    'carrier',
    'policy_identifiers',
    'category',
    'named_insured',
    'benefits',
    'adjusters',
    'claims',
    'vehicles',
    'policy_details',
    'subrogator',
    'status',
  ];

  const fields = fieldsToDisplay
    .map((fieldName) => {
      const value = coverage[fieldName];
      if (value === null || value === undefined) return null;
      if (Array.isArray(value) && value.length === 0) return null;

      return {
        name: fieldName,
        value,
      };
    })
    .filter((field): field is NonNullable<typeof field> => field !== null);

  // Separate simple fields from complex collapsible fields
  const complexFieldNames = [
    'carrier',
    'policy_identifiers',
    'benefits',
    'adjusters',
    'claims',
    'vehicles',
  ];

  const simpleFields = fields.filter(
    (field) => !complexFieldNames.includes(field.name as string),
  );
  const complexFields = fields.filter((field) =>
    complexFieldNames.includes(field.name as string),
  );

  return (
    <div className="space-y-2">
      {/* Simple fields first */}
      {simpleFields.map((field) => (
        <CoverageFieldDisplay
          key={String(field.name)}
          name={field.name}
          value={field.value}
        />
      ))}
      {/* Complex collapsible fields at the bottom */}
      {complexFields.map((field) => (
        <CoverageFieldDisplay
          key={String(field.name)}
          name={field.name}
          value={field.value}
        />
      ))}
    </div>
  );
};

/**
 * Displays a single coverage field with all its details.
 * Uses CollapsibleList for complex nested arrays.
 */
const CoverageFieldDisplay: React.FC<{
  name: keyof CoverageSchema;
  value: CoverageSchema[keyof CoverageSchema];
}> = ({ name, value }) => {
  const label = formatFieldName(name);

  // Carrier - show name and contact handles
  if (name === 'carrier' && typeof value === 'object' && value !== null) {
    const carrier = value as CarrierDetail;
    return (
      <div>
        <ActivityDescription>
          <span className="font-medium">{label}:</span> {carrier.name || 'N/A'}
        </ActivityDescription>
        {carrier.handles && carrier.handles.length > 0 && (
          <CollapsibleList
            items={carrier.handles.map((handle: ContactHandleSchema, idx) => (
              <span key={idx}>
                {handle.handle_type}: {handle.value}
              </span>
            ))}
            showLabel={`Show Contact Info (${carrier.handles.length})`}
            hideLabel="Hide Contact Info"
          />
        )}
      </div>
    );
  }

  // Policy identifiers
  if (name === 'policy_identifiers' && Array.isArray(value)) {
    const identifiers = value as PolicyIdentifier[];
    return (
      <div>
        <ActivityDescription>
          <span className="font-medium">{label}:</span>
        </ActivityDescription>
        <CollapsibleList
          items={identifiers.map((item: PolicyIdentifier, idx) => (
            <span key={idx}>
              {item.identifier_type}: {item.identifier_value}
            </span>
          ))}
          showLabel={`Show Identifiers (${identifiers.length})`}
          hideLabel="Hide Identifiers"
          defaultExpanded={identifiers.length <= 3}
        />
      </div>
    );
  }

  // Benefits - show full details
  if (name === 'benefits' && Array.isArray(value)) {
    const benefits = value as (BenefitDetail & Record<string, unknown>)[];
    return (
      <div>
        <ActivityDescription>
          <span className="font-medium">{label}:</span>
        </ActivityDescription>
        <CollapsibleList
          items={benefits.map((benefit, idx) => {
            const details: string[] = [];

            if (benefit.benefit_type) {
              details.push(`Type: ${benefit.benefit_type}`);
            }

            if (benefit.limitations) {
              Object.entries(benefit.limitations).forEach(([key, val]) => {
                details.push(
                  `${formatFieldName(key)}: ${typeof val === 'number' ? `$${val.toLocaleString()}` : String(val)}`,
                );
              });
            }

            if (benefit.limit !== undefined && benefit.limit !== null) {
              details.push(
                `Limit: ${typeof benefit.limit === 'number' ? `$${Number(benefit.limit).toLocaleString()}` : String(benefit.limit)}`,
              );
            }

            if (
              benefit.deductible !== undefined &&
              benefit.deductible !== null
            ) {
              details.push(
                `Deductible: ${typeof benefit.deductible === 'number' ? `$${Number(benefit.deductible).toLocaleString()}` : String(benefit.deductible)}`,
              );
            }

            if (benefit.per_person !== undefined) {
              details.push(`Per Person: ${benefit.per_person ? 'Yes' : 'No'}`);
            }

            if (benefit.exhausted !== undefined) {
              details.push(`Exhausted: ${benefit.exhausted ? 'Yes' : 'No'}`);
            }

            if (benefit.note) {
              details.push(`Note: ${String(benefit.note)}`);
            }

            return (
              <div key={idx} className="space-y-0.5">
                {details.map((d, i) => (
                  <div key={i}>{d}</div>
                ))}
              </div>
            );
          })}
          showLabel={`Show Benefits (${benefits.length})`}
          hideLabel="Hide Benefits"
          defaultExpanded={benefits.length <= 2}
        />
      </div>
    );
  }

  // Adjusters - show full details
  if (name === 'adjusters' && Array.isArray(value)) {
    const adjusters = value as AdjusterDetail[];
    return (
      <div>
        <ActivityDescription>
          <span className="font-medium">{label}:</span>
        </ActivityDescription>
        <CollapsibleList
          items={adjusters.map((adjuster: AdjusterDetail, idx) => {
            const details: string[] = [];

            if (adjuster.name) {
              details.push(`Name: ${adjuster.name}`);
            }

            if (adjuster.benefit_type) {
              details.push(`Benefit Type: ${adjuster.benefit_type}`);
            }

            if (adjuster.handles && adjuster.handles.length > 0) {
              adjuster.handles.forEach((handle: ContactHandleSchema) => {
                details.push(`${handle.handle_type}: ${handle.value}`);
              });
            }

            return (
              <div key={idx} className="space-y-0.5">
                {details.map((d, i) => (
                  <div key={i}>{d}</div>
                ))}
              </div>
            );
          })}
          showLabel={`Show Adjusters (${adjusters.length})`}
          hideLabel="Hide Adjusters"
          defaultExpanded={adjusters.length <= 2}
        />
      </div>
    );
  }

  // Claims - show full details
  if (name === 'claims' && Array.isArray(value)) {
    const claims = value as (ClaimDetail & Record<string, unknown>)[];
    return (
      <div>
        <ActivityDescription>
          <span className="font-medium">{label}:</span>
        </ActivityDescription>
        <CollapsibleList
          items={claims.map((claim, idx) => {
            const details: string[] = [];

            if (claim.claim_identifier) {
              details.push(`Identifier: ${claim.claim_identifier}`);
            }

            if (claim.benefit_type) {
              details.push(`Benefit: ${claim.benefit_type}`);
            }

            if (claim.status && typeof claim.status === 'string') {
              details.push(`Status: ${formatEnumValue(claim.status)}`);
            }

            if (claim.filed_date && typeof claim.filed_date === 'string') {
              details.push(
                `Filed: ${new Date(claim.filed_date).toLocaleDateString()}`,
              );
            }

            if (claim.amount !== undefined && claim.amount !== null) {
              details.push(`Amount: $${Number(claim.amount).toLocaleString()}`);
            }

            return (
              <div key={idx} className="space-y-0.5">
                {details.map((d, i) => (
                  <div key={i}>{d}</div>
                ))}
              </div>
            );
          })}
          showLabel={`Show Claims (${claims.length})`}
          hideLabel="Hide Claims"
          defaultExpanded={claims.length <= 2}
        />
      </div>
    );
  }

  // Named insured - simple list
  if (name === 'named_insured' && Array.isArray(value)) {
    const namedInsured = value as string[];
    if (namedInsured.length <= 2) {
      return (
        <ActivityDescription>
          <span className="font-medium">{label}:</span>{' '}
          {namedInsured.join(', ')}
        </ActivityDescription>
      );
    }
    return (
      <div>
        <ActivityDescription>
          <span className="font-medium">{label}:</span>
        </ActivityDescription>
        <CollapsibleList
          items={namedInsured.map((insuredName: string, idx) => (
            <span key={idx}>{insuredName}</span>
          ))}
          showLabel={`Show Named Insured (${namedInsured.length})`}
          hideLabel="Hide Named Insured"
        />
      </div>
    );
  }

  // Vehicles - show full details
  if (name === 'vehicles' && Array.isArray(value)) {
    const vehicles = value as VehicleDetail[];
    return (
      <div>
        <ActivityDescription>
          <span className="font-medium">{label}:</span>
        </ActivityDescription>
        <CollapsibleList
          items={vehicles.map((vehicle: VehicleDetail, idx) => {
            const details: string[] = [];

            const description = [vehicle.year, vehicle.make, vehicle.model]
              .filter(Boolean)
              .join(' ');
            if (description) {
              details.push(description);
            }

            if (vehicle.vin) {
              details.push(`VIN: ${vehicle.vin}`);
            }

            if (vehicle.license_plate) {
              details.push(`License: ${vehicle.license_plate}`);
            }

            if (vehicle.color) {
              details.push(`Color: ${vehicle.color}`);
            }

            return (
              <div key={idx} className="space-y-0.5">
                {details.map((d, i) => (
                  <div key={i}>{d}</div>
                ))}
              </div>
            );
          })}
          showLabel={`Show Vehicles (${vehicles.length})`}
          hideLabel="Hide Vehicles"
          defaultExpanded={vehicles.length <= 2}
        />
      </div>
    );
  }

  // Enum fields - format nicely
  if (name === 'policy_type' || name === 'category' || name === 'status') {
    return (
      <ActivityDescription>
        <span className="font-medium">{label}:</span>{' '}
        {formatEnumValue(value as string)}
      </ActivityDescription>
    );
  }

  // Default simple display
  const displayValue = formatCoverageValue(value);
  if (!displayValue) return null;

  return (
    <ActivityDescription>
      <span className="font-medium">{label}:</span> {displayValue}
    </ActivityDescription>
  );
};
